import React, {useState} from "react"
import {useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {Cog6ToothIcon, PhoneIcon} from "@heroicons/react/24/outline"

import Button from "../../../../components/Button.tsx"
import {useSalesAssistantCreateCallMutation} from "../../../../queries/salesAssistant.ts"
import {useUserSettingsOrLogout} from "../../../../queries/user.ts"
import {isSaaSCompanyUser} from "../../../../utils/types.ts"
import {getApiValidationMessages, isAxiosValidationError} from "../../../../utils/validation.ts"
import {SalesPersonSettingsModal} from "../../components/SalesPersonSettingsModal.tsx"
import {SalesPersonSelect} from "./SalesPersonSelect.tsx"

export const SettingsSection: React.FC = () => {
  const {t} = useTranslation()
  const {user} = useUserSettingsOrLogout()

  // SaaS company users pick which sales person to view via the selector. The Klara action buttons act
  // on a single sales person's assistant, so they only make sense for an actual sales person — i.e. a
  // SaaS sales person (always), or a company user linked to a sales person.
  const showSalesPersonSelect = isSaaSCompanyUser(user)
  const showKlaraButtons = !isSaaSCompanyUser(user) || user.sales_assistant_sales_person != null

  const sendCreateCallMutation = useSalesAssistantCreateCallMutation()

  const handleSend = React.useCallback(async () => {
    try {
      await sendCreateCallMutation.mutateAsync()
      toast.success(t("SalesAssistant_Settings_CreateCall_SuccessToast"))
    } catch (e) {
      let message

      if (isAxiosValidationError(e)) {
        const getErrorMessage = (err: string) => {
          switch (err) {
            case "call_pending":
              return t("SalesAssistant_Settings_CreateCall_FailureToast_CallPending")
            case "deals_empty":
              return t("SalesAssistant_Settings_CreateCall_FailureToast_DealsEmpty")
            default:
              return err
          }
        }

        message = (
          <ul>
            {getApiValidationMessages(e.response.data, "errors").map((err, index) => (
              <li key={index}>{getErrorMessage(err)}</li>
            ))}
          </ul>
        )
      }

      toast.error(message)
      return false
    }
  }, [sendCreateCallMutation, t])

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

  const handleOpenSettings = () => setIsSettingsModalOpen(true)

  const handleCloseSettings = () => setIsSettingsModalOpen(false)

  return (
    <>
      <div className={"inline-flex items-center justify-end gap-2"}>
        {showSalesPersonSelect && <SalesPersonSelect />}
        {showKlaraButtons && (
          <>
            <Button iconLeft={<PhoneIcon />} onClick={handleSend}>
              {t("SalesAssistant_Settings_CreateCall_Button")}
            </Button>
            <Button iconLeft={<Cog6ToothIcon />} onClick={handleOpenSettings} variant={"light"}>
              {t("SalesAssistant_Settings_KlaraSettings_Button")}
            </Button>
          </>
        )}
      </div>

      {isSettingsModalOpen && <SalesPersonSettingsModal isOpen={isSettingsModalOpen} onClose={handleCloseSettings} />}
    </>
  )
}
